import { createSelector } from 'reselect';
import { CompanionLocationStatus } from 'flow/companion/companion-mode';
import { State } from 'flow/types/State';
import { selectCompanionIsRequired } from 'flow/selectors/companion-selectors';
import {
    GEO_FIX_MAX_AGE_MS,
    GeoLocationCoordinates,
    GeoLocationStatus,
    geoRestrictionIsDeployed,
    geoRestrictionIsEnabled,
    geoRestrictionMayBeEnabled,
} from 'flow/utils/geolocation-utils';

export const selectGeoRestrictionIsOn = (state: State) =>
    geoRestrictionIsDeployed() &&
    geoRestrictionIsEnabled(state.app.get('geoRestrictionEnabled'));

export const selectGeoLocationIsRequired = (state: State) =>
    selectGeoRestrictionIsOn(state) && !selectCompanionIsRequired(state);

export const selectGeoLocationIsReported = (state: State) =>
    geoRestrictionIsDeployed() &&
    geoRestrictionMayBeEnabled(state.app.get('geoRestrictionEnabled')) &&
    !selectCompanionIsRequired(state);

export const selectGeoLocationStatus = (state: State) =>
    state.app.get('geoLocationStatus');

export const selectGeoLocation = (state: State) => state.app.get('geoLocation');

export const selectGeoLocationTimestamp = (state: State) =>
    state.app.get('geoLocationTimestamp');

export const selectGeoLocationPermissionDenied = (state: State) =>
    !!state.app.get('geoLocationPermissionDenied');

export const selectGeoLocationCoordinates = createSelector(
    selectGeoLocation,
    (geoLocation): GeoLocationCoordinates | null =>
        geoLocation
            ? {
                  latitude: geoLocation.get('latitude'),
                  longitude: geoLocation.get('longitude'),
              }
            : null,
);

export const selectGeoLocationIsFresh = (state: State) => {
    const timestamp = selectGeoLocationTimestamp(state);

    return !!timestamp && Date.now() - timestamp <= GEO_FIX_MAX_AGE_MS;
};

export const selectIsOutsideFence = (state: State) =>
    !!state.app.get('geoOutsideFence');

export const selectPositionIsUnavailable = (state: State) =>
    selectGeoLocationIsRequired(state) &&
    selectGeoLocationStatus(state) === GeoLocationStatus.BLOCKED;

export const selectReportableLocationStatus = (state: State): string | null => {
    const status = selectGeoLocationStatus(state);

    if (status === GeoLocationStatus.AVAILABLE) {
        return CompanionLocationStatus.OK;
    }

    if (status === GeoLocationStatus.BLOCKED) {
        return selectGeoLocationPermissionDenied(state)
            ? CompanionLocationStatus.DENIED
            : CompanionLocationStatus.UNAVAILABLE;
    }

    return null;
};
